import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Save, Plus, Trash2, ArrowLeft, X, BarChart3, Users, Shield, Award, Check, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import * as HeroIconsOutline from '@heroicons/react/24/outline';

const LucideIconMap = {
    BarChart3, Users, Shield, Award
};

const CommitteeModal = ({ isOpen, onClose, onSubmit, committee, isEditing }) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        icon: 'BarChart3',
        color: 'blue',
        is_active: true
    });

    useEffect(() => {
        if (committee) {
            setFormData(committee);
        } else {
            setFormData({
                name: '',
                description: '',
                icon: 'BarChart3',
                color: 'blue',
                is_active: true
            });
        }
    }, [committee, isOpen]);

    const iconOptions = ['BarChart3', 'Users', 'Shield', 'Award'];
    const colorOptions = ['blue', 'green', 'gold', 'purple', 'red'];

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl transform transition-all">
                <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                    <h3 className="text-xl font-bold text-slate-800">
                        {isEditing ? 'Edit Committee' : 'Add New Committee'}
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Committee Name</label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="e.g. Audit Committee"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Description</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            rows={3}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 resize-none"
                            placeholder="Description..."
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Icon</label>
                            <select
                                value={formData.icon}
                                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                            >
                                {iconOptions.map(icon => (
                                    <option key={icon} value={icon}>{icon}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Color Theme</label>
                            <select
                                value={formData.color}
                                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                            >
                                {colorOptions.map(color => (
                                    <option key={color} value={color}>{color.charAt(0).toUpperCase() + color.slice(1)}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                        <input
                            type="checkbox"
                            id="is_active"
                            checked={formData.is_active !== false}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                            className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 border-gray-300"
                        />
                        <label htmlFor="is_active" className="text-sm font-medium text-slate-700">Set as Active</label>
                    </div>
                </div>

                <div className="p-6 border-t border-slate-100 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-slate-600 font-medium hover:bg-slate-50 rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => onSubmit(formData)}
                        className="px-6 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30"
                    >
                        {isEditing ? 'Save Changes' : 'Add Committee'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const GovernanceOversightPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sectionData, setSectionData] = useState({
        section_tag: '',
        title: '',
        description: '',
        extra_text: '',
        content_data: []
    });

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const response = await axios.get('/api/admin/governance/section/oversight');
            if (response.data.success && response.data.data) {
                const data = response.data.data;
                const parsedContent = typeof data.content_data === 'string'
                    ? JSON.parse(data.content_data)
                    : (data.content_data || []);

                setSectionData({
                    section_tag: data.section_tag || '',
                    title: data.title || '',
                    description: data.description || '',
                    extra_text: data.extra_text || '',
                    content_data: parsedContent
                });
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching oversight data:', error);
            toast.error('Failed to load data');
            setLoading(false);
        }
    };

    const handleSettingsSave = async () => {
        try {
            setSaving(true);
            await axios.put('/api/admin/governance/section/oversight', {
                ...sectionData,
                content_data: sectionData.content_data
            });
            toast.success('Section settings updated successfully');
        } catch (error) {
            console.error('Error saving oversight data:', error);
            toast.error('Failed to save data');
        } finally {
            setSaving(false);
        }
    };

    const saveContentData = async (newContentData) => {
        try {
            await axios.put('/api/admin/governance/section/oversight', {
                ...sectionData,
                content_data: newContentData
            });
            setSectionData(prev => ({ ...prev, content_data: newContentData }));
            return true;
        } catch (error) {
            console.error('Error updating committees:', error);
            toast.error('Failed to update committees');
            return false;
        }
    };

    const handleModalSubmit = async (committeeData) => {
        let newContentData = [...sectionData.content_data];
        if (editingIndex !== null) {
            newContentData[editingIndex] = committeeData;
            toast.success('Committee updated');
        } else {
            newContentData.push(committeeData);
            toast.success('Committee added');
        }

        const success = await saveContentData(newContentData);
        if (success) {
            setIsModalOpen(false);
            setEditingIndex(null);
        }
    };

    const handleDelete = async (index) => {
        if (window.confirm('Are you sure you want to delete this committee?')) {
            const newContentData = sectionData.content_data.filter((_, i) => i !== index);
            await saveContentData(newContentData);
            toast.success('Committee deleted');
        }
    };

    const getColorGradient = (color) => {
        const gradients = {
            blue: 'from-blue-500 to-indigo-600',
            green: 'from-emerald-400 to-green-600',
            gold: 'from-amber-400 to-orange-500',
            purple: 'from-purple-500 to-indigo-600',
            red: 'from-red-500 to-rose-600'
        };
        return gradients[color] || gradients['blue'];
    };

    if (loading) return <div className="p-6">Loading...</div>;

    return (
        <div className="space-y-6 pb-12">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Governance - Oversight Section</h1>
                    <p className="text-slate-500 mt-1">Manage "Oversight & Structure" section content</p>
                </div>
            </div>

            {/* Section Settings */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <h2 className="text-lg font-bold text-slate-800 mb-4 border-b pb-2">Section Header Settings</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Section Tag</label>
                        <input
                            type="text"
                            value={sectionData.section_tag}
                            onChange={(e) => setSectionData({ ...sectionData, section_tag: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="e.g., Oversight & Structure"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Section Title</label>
                        <input
                            type="text"
                            value={sectionData.title}
                            onChange={(e) => setSectionData({ ...sectionData, title: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="e.g., Empowered Leadership..."
                        />
                    </div>
                </div>
                <div className="mb-4">
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Description</label>
                    <textarea
                        value={sectionData.description}
                        onChange={(e) => setSectionData({ ...sectionData, description: e.target.value })}
                        rows={3}
                        className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 resize-none"
                    />
                </div>
                <div className="flex justify-end">
                    <button
                        onClick={handleSettingsSave}
                        disabled={saving}
                        className="flex items-center gap-2 bg-primary-600 text-white px-6 py-2 rounded-xl hover:bg-primary-700 disabled:opacity-50 shadow-md transition-all font-medium"
                    >
                        <Save size={18} />
                        {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </div>

            {/* Committees Cards */}
            <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <div>
                        <h2 className="text-xl font-bold text-slate-800">Committees</h2>
                        <p className="text-sm text-slate-500">Manage the committee cards</p>
                    </div>
                    <button
                        onClick={() => {
                            setEditingIndex(null);
                            setIsModalOpen(true);
                        }}
                        className="flex items-center gap-2 bg-primary-600 text-white px-6 py-3 rounded-xl hover:bg-primary-700 shadow-lg hover:shadow-xl transition-all font-semibold"
                    >
                        <Plus size={20} /> Add Committee
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {sectionData.content_data.map((committee, index) => {
                        const IconComponent = LucideIconMap[committee.icon] || BarChart3;
                        const gradient = getColorGradient(committee.color);

                        return (
                            <div
                                key={index}
                                className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-all group"
                            >
                                {/* Gradient Top Bar */}
                                <div className={`h-2 bg-gradient-to-r ${gradient}`}></div>

                                <div className="p-5">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className={`p-3 rounded-xl bg-gradient-to-br ${gradient} text-white shadow-sm`}>
                                            <IconComponent size={24} />
                                        </div>
                                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={() => {
                                                    setEditingIndex(index);
                                                    setIsModalOpen(true);
                                                }}
                                                className="p-2 hover:bg-slate-50 rounded-lg text-slate-400 hover:text-primary-600 transition-colors"
                                                title="Edit"
                                            >
                                                <HeroIconsOutline.PencilIcon className="h-5 w-5" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(index)}
                                                className="p-2 hover:bg-slate-50 rounded-lg text-slate-400 hover:text-red-600 transition-colors"
                                                title="Delete"
                                            >
                                                <Trash2 size={20} />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Status & Order Badge */}
                                    <div className="flex items-center justify-between mb-4">
                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${committee.is_active !== false ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500'}`}>
                                            {committee.is_active !== false ? 'Active' : 'Inactive'}
                                        </span>
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                            ORDER #{index + 1}
                                        </span>
                                    </div>

                                    <div className="space-y-2">
                                        <h3 className="font-bold text-slate-800 text-lg">{committee.name}</h3>
                                        <p className="text-slate-600 text-sm leading-relaxed line-clamp-2">
                                            {committee.description}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )
                    })}

                    {sectionData.content_data.length === 0 && (
                        <div className="col-span-full py-12 text-center bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                                <BarChart3 className="h-8 w-8 text-slate-300" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-800 mb-1">No committees added</h3>
                            <button
                                onClick={() => {
                                    setEditingIndex(null);
                                    setIsModalOpen(true);
                                }}
                                className="text-primary-600 font-medium hover:text-primary-700"
                            >
                                + Add First Committee
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <CommitteeModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleModalSubmit}
                committee={editingIndex !== null ? sectionData.content_data[editingIndex] : null}
                isEditing={editingIndex !== null}
            />
        </div>
    );
};

export default GovernanceOversightPage;
